import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";

export interface Skill {
	id: number;
	uuid: string;
	name: string;
	description?: string;
	isActive?: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface CreateSkillPayload {
	name: string;
	description: string;
	isActive: boolean;
}

interface CreateSkillResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: Skill;
}

interface SkillsApiResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: {
		data: Skill[];
		pagination: {
			currentPage: number;
			perPage: number;
			totalItems: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
}

export const useSkills = () => {
	return useQuery({
		queryKey: [QUERY_KEYS.JOBS.SKILLS],
		queryFn: async (): Promise<Skill[]> => {
			const response = await api.get<SkillsApiResponse>(SERVER_ROUTES.JOBS.SKILLS);

			// Handle axios interceptor response structure
			let responseData: SkillsApiResponse;

			if (response && typeof response === "object" && "data" in response) {
				if ("data" in response && response.data && typeof response.data === "object" && "data" in response.data) {
					// Response is wrapped: { data: { data: [...], pagination: {...} } }
					responseData = response as unknown as SkillsApiResponse;
				} else {
					// Response is already the data structure
					responseData = { data: response } as unknown as SkillsApiResponse;
				}
			} else if (response && typeof response === "object" && "success" in response) {
				// Response already has the correct structure
				responseData = response as unknown as SkillsApiResponse;
			} else {
				// Fallback - create a complete response structure
				responseData = {
					success: true,
					statusCode: 200,
					message: "No data available",
					data: {
						data: [],
						pagination: {
							currentPage: 1,
							perPage: 10,
							totalItems: 0,
							totalPages: 0,
							hasNextPage: false,
							hasPreviousPage: false,
						},
					},
				};
			}

			return responseData?.data?.data || [];
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

// Mutation to create a new skill
export const useCreateSkill = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: CreateSkillPayload): Promise<Skill> => {
			const response = await api.post<CreateSkillResponse>(
				SERVER_ROUTES.JOBS.SKILLS,
				payload
			);

			// Handle axios interceptor response structure
			let responseData: CreateSkillResponse;

			if (response && typeof response === "object" && "data" in response && response.data) {
				responseData = response as unknown as CreateSkillResponse;
			} else if (response && typeof response === "object" && "success" in response) {
				responseData = response as unknown as CreateSkillResponse;
			} else {
				throw new Error("Invalid response from server");
			}

			if (!responseData.success) {
				throw new Error(responseData.message || "Failed to create skill");
			}

			return responseData.data;
		},
		onSuccess: () => {
			// Invalidate skills query to refetch the updated list
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS.SKILLS] });
		},
	});
};
